import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import { fetchShootsByProject, isoOf } from '../../utils/projectShoots';
import { fetchMirroredEvents, fetchShootEventIds, type MirroredEvent } from '../../utils/calendarMirror';
import { emptyEntryDraft, fetchCalendarEntries, type CalendarEntry } from '../../utils/calendarEntries';
import { CalendarMonth, gridStart, type Entry } from '../../components/portal/CalendarMonth';
import { CalendarAgenda } from '../../components/portal/CalendarAgenda';
import { ShootEditor, type ShootEdit } from '../../components/portal/ShootEditor';
import { EntryEditor, type EntryEdit } from '../../components/portal/EntryEditor';
import { DayAddChooser } from '../../components/portal/DayAddChooser';
import { useIsPhone } from '../../utils/isPhone';

/**
 * The studio calendar — where shoot days are set and the studio's own diary
 * entries are written, with the company Google calendar alongside.
 *
 * Three layers, and the difference between them is the thing to hold on to:
 *
 *   • SHOOT DAYS belong to a project. Portal OS owns the date; it adds an
 *     event to Google only where the calendar has nothing, and never edits a
 *     hand-made one.
 *   • ENTRIES (13 Sep 2026) are Portal OS's own diary — "Tom meeting with
 *     client". Portal OS created the Google event, so it updates and deletes
 *     it too.
 *   • GOOGLE'S OWN entries are somebody else's. Read-only here, forever; the
 *     chip links out rather than pretending to be editable.
 *
 * Anything Portal OS put in Google comes back in the mirror on the next run,
 * so both its own layers are filtered out of the Google layer — otherwise
 * every shoot and every entry would appear twice, which is the duplication
 * the whole fill-the-gaps rule exists to prevent, reintroduced visually.
 *
 * **THE LEGEND IS GONE (16 Oct 2026).** Three lines above the grid explained
 * which shade of grey meant which layer. It went for the same reason the
 * footnotes went before it (22 Sep) and the Dispatches standfirst went after
 * — a paragraph explaining a screen is an admission the screen does not
 * explain itself, and this one was the longest-serving example in the app.
 * The distinction it drew matters when you are setting a date, which is when
 * you click something and find out; it does not matter at all when you are
 * looking at a Tuesday. `components/portal/CalendarLegend.tsx` now has no
 * caller — leave it or delete it, but do not wire it back in.
 *
 * **24 Sep 2026: below 640px the grid becomes a list.** Seven columns do not
 * survive a 390px screen at any type size, so `CalendarAgenda` draws the same
 * three layers down the page instead. Everything below — the chooser and both
 * editors — is shared: there is one calendar, drawn two ways.
 *
 * **24 Sep 2026: `focusDate` — arriving here pointed at a day.** Pressing a
 * row on the Week Ahead opens this page on that day. Two things follow from
 * that, and both were mistakes waiting to happen:
 *
 *   • The MONTH has to be set from it before first paint, not after. A day in
 *     October opened in September would render, then jump.
 *   • The MARK has to be temporary. It is there to answer "which one did I
 *     press?", and once you have paged the month or opened anything it is
 *     answering a question you stopped asking — a permanent highlight on a
 *     day you dealt with an hour ago reads as state, and people try to
 *     dismiss it. So the first interaction clears it.
 *
 * It is deliberately only a mark. It does not open the day's chooser: a press
 * on the week is a press on a date, which is not yet a decision to put
 * something on it, and opening an editor nobody asked for is how you get a
 * blank entry saved by accident.
 */
export function Calendar({
  focusDate = ''
}: {
  focusDate?: string;
}) {
  const {
    activeProjects
  } = usePortal();
  const isPhone = useIsPhone();
  const [month, setMonth] = useState(() => {
    /* Split rather than Date-parsed where it can be, for the same reason
       dayLabel is: a bare ISO string parsed as UTC shifts a day in Dubai. */
    const [year, index, day] = focusDate.split('-').map(Number);
    if (year && index && day) return new Date(year, index - 1, 1);
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  /** The day pressed on the Week Ahead, until the first interaction here. */
  const [focus, setFocus] = useState(focusDate);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [notes, setNotes] = useState<CalendarEntry[]>([]);
  const [events, setEvents] = useState<MirroredEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState<ShootEdit | null>(null);
  const [noteEdit, setNoteEdit] = useState<EntryEdit | null>(null);
  /** Which day the "shoot or entry?" chooser is open for, or ''. */
  const [adding, setAdding] = useState('');

  /* The six-week grid runs past both ends of the month, so the window asked
     for is the grid's, not the month's — otherwise the first and last rows
     would quietly lose their entries. The agenda shows the month itself, a
     subset, so the same window serves both. */
  const from = isoOf(gridStart(month));
  const toDate = new Date(gridStart(month));
  toDate.setDate(toDate.getDate() + 41);
  const to = isoOf(toDate);
  const load = useCallback(async () => {
    const [grouped, mirror, shootEventIds, ownEntries] = await Promise.all([fetchShootsByProject(), fetchMirroredEvents(from, to), fetchShootEventIds(), fetchCalendarEntries(from, to)]);
    const flat: Entry[] = [];
    for (const [projectId, shoots] of grouped) {
      const project = activeProjects.find(item => item.id === projectId);
      for (const shoot of shoots) flat.push({
        shoot,
        projectId,
        project
      });
    }

    // Everything Portal OS owns in Google, so the mirror doesn't draw it again.
    const ours = new Set(shootEventIds);
    for (const entry of ownEntries) if (entry.googleEventId) ours.add(entry.googleEventId);
    setEntries(flat);
    setNotes(ownEntries);
    setEvents(mirror.events.filter(event => !ours.has(event.eventId)));
    setLoading(false);
  }, [activeProjects, from, to]);
  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Bring whatever just opened into view (23 Sep 2026, Tom's report).
   *
   * Every editor on this page appears BELOW a six-week grid, which on most
   * screens puts it off the bottom. Pressing + therefore looked like it had
   * done nothing — Tom: "I click it and don't know if it has worked until I
   * remember to scroll down." A control whose effect is invisible is a
   * control people press twice. On a phone the agenda is longer still, so it
   * matters more there, not less.
   *
   * Keyed on WHICH panel is open rather than on "a panel is open", so that
   * moving from the chooser to the editor, or from one day's entry to
   * another's, scrolls again rather than sitting still.
   */
  const panelRef = useRef<HTMLDivElement | null>(null);
  const panelKey = [adding, edit ? `shoot:${edit.id ?? `new-${edit.draft.date}`}` : '', noteEdit ? `entry:${noteEdit.id ?? `new-${noteEdit.draft.startDate}`}` : ''].join('|');
  useEffect(() => {
    if (!panelRef.current) return;
    /* A beat's delay: the panel is only in the DOM after this render, and
       scrolling to an element that does not exist yet does nothing. */
    const timer = window.setTimeout(() => {
      panelRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 60);
    return () => window.clearTimeout(timer);
  }, [panelKey]);
  function closeAll() {
    setEdit(null);
    setNoteEdit(null);
    setAdding('');
  }
  function shift(months: number) {
    setFocus('');
    setMonth(current => new Date(current.getFullYear(), current.getMonth() + months, 1));
  }
  function openShoot(entry: Entry) {
    setFocus('');
    setNoteEdit(null);
    setAdding('');
    setEdit({
      id: entry.shoot.id,
      projectId: entry.projectId,
      draft: {
        date: entry.shoot.date,
        endDate: entry.shoot.endDate ?? '',
        title: entry.shoot.title,
        time: entry.shoot.time,
        location: entry.shoot.location
      }
    });
  }
  function openEntry(entry: CalendarEntry) {
    setFocus('');
    setEdit(null);
    setAdding('');
    setNoteEdit({
      id: entry.id,
      draft: {
        title: entry.title,
        startDate: entry.startDate,
        endDate: entry.endDate === entry.startDate ? '' : entry.endDate,
        startTime: entry.startTime,
        endTime: entry.endTime,
        location: entry.location,
        notes: entry.notes
      }
    });
  }
  function openAdd(iso: string) {
    setFocus('');
    setEdit(null);
    setNoteEdit(null);
    setAdding(iso);
  }
  const monthName = month.toLocaleDateString('en-GB', {
    month: 'long',
    year: 'numeric'
  });
  return <div data-id="element-2257" data-mp="5pjf18ekf6b">
      <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-2258" data-mp="563fq2bc0ux">
        <h1 className="font-display text-4xl leading-[0.95] tracking-tight sm:text-6xl" data-id="element-2259" data-mp="3sbbbvqp56pte">Calendar</h1>

        <div className="flex items-center gap-3.5" data-id="element-2260" data-mp="a56rfpb4n55g">
          <button type="button" onClick={() => shift(-1)} aria-label="Previous month" className="inline-flex h-11 w-11 items-center justify-center border border-paper/30 text-paper/70 transition-colors duration-150 ease-out hover:border-paper hover:text-paper" data-id="element-2261" data-mp="a2kjvpo8kpn8">
            <ChevronLeftIcon className="h-4 w-4" strokeWidth={1.5} data-id="element-2262" data-mp="3nb6u7xchzp1l" />
          </button>
          <span className="label min-w-[8rem] text-center text-paper sm:min-w-[9.5rem]" data-id="element-2263" data-mp="3ikqgec4rm1ep">
            {monthName}
          </span>
          <button type="button" onClick={() => shift(1)} aria-label="Next month" className="inline-flex h-11 w-11 items-center justify-center border border-paper/30 text-paper/70 transition-colors duration-150 ease-out hover:border-paper hover:text-paper" data-id="element-2264" data-mp="2uudzwcvgewci">
            <ChevronRightIcon className="h-4 w-4" strokeWidth={1.5} data-id="element-2265" data-mp="ltz1t67h3oc7" />
          </button>
          <button type="button" onClick={() => {
          const now = new Date();
          setFocus('');
          setMonth(new Date(now.getFullYear(), now.getMonth(), 1));
        }} className="label ml-1 inline-flex h-11 items-center border border-paper/30 px-4 text-paper/70 transition-colors duration-150 ease-out hover:border-paper hover:text-paper" data-id="element-2266" data-mp="gsrqwya1a91b">
            Today
          </button>
        </div>
      </div>

      <div className="mt-12" data-id="element-2267" data-mp="3p9wdxkp736d6">
        {isPhone ? <CalendarAgenda month={month} entries={entries} notes={notes} events={events} selectedId={edit?.id ?? ''} selectedNoteId={noteEdit?.id ?? ''} focusDate={focus} onSelect={openShoot} onSelectNote={openEntry} onAdd={openAdd} data-id="element-2268" data-mp="3pb7fddv64214" /> : <CalendarMonth month={month} entries={entries} notes={notes} events={events} selectedId={edit?.id ?? ''} selectedNoteId={noteEdit?.id ?? ''} focusDate={focus} onSelect={openShoot} onSelectNote={openEntry} onAdd={openAdd} data-id="element-2269" data-mp="1zqxsgaa0td1y" />}
      </div>

      {loading && <p className="label mt-12 text-paper/35" data-id="element-2270" data-mp="1h2j4jvv02f5d">Loading…</p>}

      {/* scroll-mt keeps the studio nav clear of whatever we scroll to. */}
      <div ref={panelRef} className="scroll-mt-28" data-id="element-2271" data-mp="1lz15ekn7ltcf">
        {adding && <DayAddChooser date={adding} onEntry={() => {
        setNoteEdit({
          draft: emptyEntryDraft(adding)
        });
        setAdding('');
      }} onShoot={() => {
        setEdit({
          projectId: '',
          draft: {
            date: adding,
            endDate: '',
            title: '',
            time: '',
            location: ''
          }
        });
        setAdding('');
      }} onCancel={() => setAdding('')} data-id="element-2272" data-mp="1qt5cd3wh61di" />}

        {edit && <ShootEditor key={edit.id ?? `new-${edit.draft.date}`} projects={activeProjects} edit={edit} onSaved={() => {
        closeAll();
        void load();
      }} onCancel={() => setEdit(null)} data-id="element-2273" data-mp="zzlowxnwb5t8" />}

        {noteEdit && <EntryEditor key={noteEdit.id ?? `new-entry-${noteEdit.draft.startDate}`} edit={noteEdit} onSaved={() => {
        closeAll();
        void load();
      }} onCancel={() => setNoteEdit(null)} data-id="element-2274" data-mp="3ch351e8e478" />}
      </div>
    </div>;
}